import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Loader2, Package, X } from "lucide-react";

// In-memory cache of product-lookup responses keyed by item_number.
// Survives across tooltip mounts within the same session.
const PRODUCT_CACHE = new Map();

/**
 * Floating card that appears near the cursor when the user double-clicks
 * an item-number span in the PDF text layer. Fetches product info from
 * the backend proxy and shows a compact card: photo · description · spec
 * · duty · size · factory quote · sale prices.
 */
export default function ProductTooltip({ itemNumber, anchor, onClose }) {
  const [state, setState] = useState({ status: "loading", data: null });
  const cardRef = useRef(null);
  const [pos, setPos] = useState({ top: anchor.top, left: anchor.left });

  // Load product data (with cache).
  useEffect(() => {
    let cancelled = false;
    const cached = PRODUCT_CACHE.get(itemNumber);
    if (cached) {
      setState({ status: cached === "none" ? "error" : "ready", data: cached === "none" ? null : cached });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/product-lookup/${encodeURIComponent(itemNumber)}`);
        if (cancelled) return;
        PRODUCT_CACHE.set(itemNumber, data);
        setState({ status: "ready", data });
      } catch (e) {
        if (cancelled) return;
        PRODUCT_CACHE.set(itemNumber, "none");
        setState({ status: "error", data: null });
      }
    })();
    return () => { cancelled = true; };
  }, [itemNumber]);

  // Position: try to appear below-right of the anchor, but clamp to viewport.
  useEffect(() => {
    const CARD_W = 320;
    const CARD_H = 380;
    const PAD = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top = anchor.bottom + 8;
    let left = anchor.left;
    if (top + CARD_H > vh - PAD) top = Math.max(PAD, anchor.top - CARD_H - 8);
    if (left + CARD_W > vw - PAD) left = Math.max(PAD, vw - CARD_W - PAD);
    setPos({ top, left });
  }, [anchor]);

  // Close on Escape key or when clicking outside the card.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    const onDown = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    // Listen on capture so we intercept before other handlers (e.g. SVG).
    // Use `pointerdown` so both mouse and touch trigger the close.
    window.addEventListener("pointerdown", onDown, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onDown, true);
    };
  }, [onClose]);

  const stop = (e) => e.stopPropagation();

  return (
    <div
      ref={cardRef}
      data-testid="product-tooltip"
      className="fixed z-[60] w-[320px] rounded-lg overflow-hidden bg-white"
      style={{
        top: pos.top,
        left: pos.left,
        border: "2px solid #2f7cff",
        boxShadow: "0 20px 60px -20px rgba(47,124,255,0.35), 0 8px 24px -8px rgba(15,23,42,0.15)",
      }}
      onMouseEnter={stop}
      onMouseDown={stop}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-[#f5f8ff] border-b border-[#2f7cff33]">
        <div className="flex items-center gap-1.5 min-w-0">
          <Package className="w-3.5 h-3.5 text-[#2f7cff] flex-shrink-0" />
          <span className="text-[12px] font-semibold text-slate-700 truncate">
            {itemNumber}
          </span>
        </div>
        <button
          data-testid="product-tooltip-close"
          onClick={onClose}
          className="h-6 w-6 flex items-center justify-center rounded-full text-slate-500 hover:bg-white transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {state.status === "loading" && (
        <div className="flex items-center gap-2 px-4 py-6 text-[13px] text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          加载产品信息…
        </div>
      )}

      {state.status === "error" && (
        <div className="px-4 py-6 text-[13px] text-slate-500 italic">
          未找到 <span className="font-mono font-semibold">{itemNumber}</span> 的产品资料
        </div>
      )}

      {state.status === "ready" && state.data && (
        <ProductCardBody data={state.data} />
      )}
    </div>
  );
}

function ProductCardBody({ data }) {
  const priceRow = (label, val, unit) => (
    val === null || val === undefined || val === 0 ? null : (
      <div className="flex justify-between text-[12px]">
        <span className="text-slate-500">{label}</span>
        <span className="font-semibold text-slate-800">
          {typeof val === "number" ? val.toFixed(2) : val} {unit || ""}
        </span>
      </div>
    )
  );
  return (
    <div className="text-[12px]">
      {data.photo_url && (
        <div className="w-full h-32 bg-slate-100 flex items-center justify-center overflow-hidden">
          <img
            src={data.photo_url}
            alt={data.item_number}
            className="w-full h-full object-contain"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>
      )}
      <div className="px-3 py-2.5 space-y-2">
        {data.description && (
          <div className="text-[12px] text-slate-800 leading-snug line-clamp-2">
            {data.description}
          </div>
        )}
        {data.description_zh && (
          <div className="text-[11px] text-slate-600 leading-snug line-clamp-2">
            {data.description_zh}
          </div>
        )}
        {(data.specification || data.specification_zh) && (
          <div className="text-[11px] text-slate-500">
            规格：{data.specification || data.specification_zh}
          </div>
        )}
        <div className="border-t border-slate-100 pt-2 space-y-1">
          {priceRow("尺寸", data.size)}
          {priceRow("关税 Duty", data.duty ? `${(data.duty * 100).toFixed(1)}%` : null)}
          {priceRow("退税", data.tax_rebate ? `${(data.tax_rebate * 100).toFixed(1)}%` : null)}
          {priceRow("工厂价", data.factory_cost, data.factory_cost_currency)}
          {priceRow("售价 USD", data.price_usd, "USD")}
          {priceRow("售价 CAD", data.price_cad, "CAD")}
          {data.factory && (
            <div className="flex justify-between text-[11px] text-slate-400">
              <span>工厂</span>
              <span>{data.factory}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Regex for detecting item numbers. Matches patterns like:
 *   MY-2504, Y24-TB012, G24-BA008PDQ, HPAH4100B-25
 * Requires: 1-4 uppercase letters, digit(s), optional dash + alnum block.
 */
export const ITEM_NUMBER_REGEX = /\b([A-Z]{1,4}[A-Z0-9]{0,2}-?\d{2,}(?:-[A-Z0-9]{2,})?)\b/g;
